import {ActivityHeader} from '@github-ui/commenting/ActivityHeader'
import {ReactionViewerBase} from '@github-ui/reaction-viewer/ReactionViewerBase'
import {VerifiedHTMLDiv} from '@github-ui/safe-html/VerifiedHTML'
import {
  reviewSummaryStateLabels,
  type ReviewSummary,
  type ReviewSummaryState,
} from '../../page-data/payloads/review-summaries'
import type {ReactionContent} from '@github-ui/reaction-viewer/ReactionGroupsUtils'
import {useReactToReviewSummaryMutation} from '../../mutations/use-react-to-review-summary-mutation'
import {CheckIcon, CommentIcon, DiffIcon} from '@primer/octicons-react'
import {type SelectionContext, selectQuoteFromComment} from '@github-ui/commenting/quotes'
import {useRef, useCallback, useMemo, use} from 'react'
import {ssrSafeWindow} from '@github-ui/ssr-utils'
import CircleOcticon from '@github-ui/circle-octicon'
import {useLayoutCanComment} from '../../stores/layout-selectors'
import {OnBehalfOfTeams} from '../overview/reviewers/OnBehalfOfTeams'
import {legacyReviewerToProto} from '../../page-data/payloads/review-requests'
import {clsx} from 'clsx'
import {ThreadPreview} from './ThreadPreview'
import {HydrateThreadPositioning} from './HydrateThreadPositioning'
import type {ReviewThread} from './utils/assign-threads-to-reviews'
import {CommentLayout} from './CommentLayout'
import {ConversationItemActions, ConversationItemActionsProvider} from './ConversationItemActions'
import styles from './ReviewSummaryItem.module.css'
import {reviewSummaryAnchor} from './utils/comment-anchors'
import {CommentsContext} from './CommentsContext'
import {useFocusHashTarget} from '../../hooks/use-focus-hash-target'

interface ReviewSummaryItemProps {
  reviewSummary: ReviewSummary
  reviewThreads?: ReviewThread[]
  pullRequestAuthorLogin: string
  canReact: boolean
  // Optional flag to show the review state icon next to the review summary. Defaults to false.
  withReviewStateIcon?: boolean
  // Optional callback for handling quote reply actions. If provided, a "Quote reply" button will be rendered.
  onQuoteReply?: (quotedText: string) => void
  className?: string
  onNavigateToDiffComment?: () => void
}

function ReviewStateIcon({state}: {state: ReviewSummaryState}) {
  switch (state) {
    case 'APPROVED':
      return (
        <div className={styles.reviewStateIcon}>
          <CircleOcticon icon={() => <CheckIcon size={12} />} className={styles.approved} size={18} />
        </div>
      )
    case 'CHANGES_REQUESTED':
      return (
        <div className={styles.reviewStateIcon}>
          <CircleOcticon icon={() => <DiffIcon size={12} />} className={styles.changesRequested} size={18} />
        </div>
      )
    case 'COMMENTED':
      return (
        <div className={styles.reviewStateIcon}>
          <CircleOcticon icon={() => <CommentIcon size={12} />} className={styles.commented} size={18} />
        </div>
      )
    case 'DISMISSED':
    default:
      return null
  }
}

export function ReviewSummaryItem({
  reviewSummary,
  pullRequestAuthorLogin,
  canReact,
  onQuoteReply: externalOnQuoteReply,
  withReviewStateIcon,
  className,
  reviewThreads,
  onNavigateToDiffComment,
}: ReviewSummaryItemProps): React.ReactElement {
  const {mutate: reactToReviewSummaryMutation} = useReactToReviewSummaryMutation()
  const commentRef = useRef<HTMLElement>(null)
  const canComment = useLayoutCanComment()

  const onReact = useCallback(
    (reaction: ReactionContent, viewerHasReacted: boolean) => {
      reactToReviewSummaryMutation({
        commentId: reviewSummary.id,
        reaction,
        viewerHasReacted,
      })
    },
    [reactToReviewSummaryMutation, reviewSummary.id],
  )

  const hasBody = reviewSummary.body.trim().length > 0

  const onQuoteReply = useMemo(() => {
    if (!canComment || !externalOnQuoteReply || !hasBody) return undefined

    return () => {
      const current = ssrSafeWindow?.getSelection()
      let currentSelection: SelectionContext | null = null

      if (current && current.anchorNode && current.rangeCount > 0) {
        currentSelection = {
          anchorNode: current.anchorNode,
          range: current.getRangeAt(0),
        }
      }

      const quotedText = selectQuoteFromComment(commentRef.current, currentSelection, reviewSummary.body)
      externalOnQuoteReply(quotedText ?? reviewSummary.body)
    }
  }, [canComment, externalOnQuoteReply, hasBody, reviewSummary.body])
  const reviewStateIcon = withReviewStateIcon ? <ReviewStateIcon state={reviewSummary.state} /> : null

  const threads = useMemo(() => reviewThreads?.map(({thread}) => thread) ?? [], [reviewThreads])
  const hasThreads = threads.length > 0

  const {commentAnchorPrefix} = use(CommentsContext)
  const id = reviewSummaryAnchor(reviewSummary.id, commentAnchorPrefix)
  useFocusHashTarget(id)

  return (
    <ConversationItemActionsProvider onQuoteReply={onQuoteReply}>
      <CommentLayout
        ref={commentRef}
        className={clsx('border rounded-2', className)}
        tabIndex={-1}
        id={id}
        header={
          <>
            <ActivityHeader
              avatarUrl={reviewSummary.authorAvatarUrl || ''}
              additionalUserIcon={reviewStateIcon ? reviewStateIcon : undefined}
              comment={{
                id: reviewSummary.id,
                authorAssociation: reviewSummary.authorAssociation,
                createdAt: reviewSummary.submittedAt,
                isHidden: reviewSummary.isHidden,
                minimizedReason: reviewSummary.minimizedReason,
                repository: reviewSummary.repository,
                url: reviewSummary.url,
              }}
              commentAuthorLogin={reviewSummary.authorLogin}
              commentSubjectType="pull request"
              commentSubjectAuthorLogin={pullRequestAuthorLogin}
              forceInlineAvatar
              isMinimized={false}
              noContainerStyles
              tooltipDirection="sw"
              additionalHeaderMessage={<>{reviewSummaryStateLabels[reviewSummary.state]}&nbsp;</>}
              actions={<ConversationItemActions onQuoteReply={onQuoteReply} />}
            />
            <OnBehalfOfTeams
              teams={reviewSummary.onBehalfOfTeams.map(legacyReviewerToProto)}
              className="tmp-px-3 pt-1"
              withAvatar={false}
            />
          </>
        }
      >
        {(hasBody || hasThreads) && (
          <div className="d-flex flex-column position-relative width-full gap-3">
            {hasBody && (
              <div>
                <VerifiedHTMLDiv className="markdown-body f5" html={reviewSummary.bodyHtml} />
                {reviewSummary.reactionGroups.length > 0 && (
                  <div className="mt-2">
                    <ReactionViewerBase
                      reactionGroups={reviewSummary.reactionGroups}
                      canReact={canReact && reviewSummary.viewerCanReact}
                      onReact={onReact}
                    />
                  </div>
                )}
              </div>
            )}
            {hasThreads && (
              <>
                <HydrateThreadPositioning threads={threads} />
                {reviewThreads?.map(({thread, anchorPrefix}) => (
                  <ThreadPreview
                    key={thread.id}
                    thread={thread}
                    // The same thread may appear under multiple reviews, so we attach a review ID prefix to ensure uniqueness
                    anchorPrefix={anchorPrefix}
                    selectedReviewId={parseInt(reviewSummary.id, 10)}
                    onNavigateToDiffComment={onNavigateToDiffComment}
                    headingLevel="h2"
                  />
                ))}
              </>
            )}
          </div>
        )}
      </CommentLayout>
    </ConversationItemActionsProvider>
  )
}
